import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {batchUpdateCards} from 'common/src/modules/campaigns/store/campaignLogicTab/actions/batchUpdateCards';
import {DisconnectedNodeOutputData, MapNode} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import refreshCampaignIssues from '../../../utils/refreshCampaignIssues';
import buildConnectionMap from './utils/buildConnectionMap';
import duplicateSubtreeRecursive from './utils/duplicateSubtreeRecursive';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const duplicateSubtreeToolSchema = z.object({
  cardId: z.string().describe(describeIdFormat(IdPrefix.Card)),
});

function duplicateSubtreeTool(store: CLStore) {
  async function execute({
    input: {cardId},
  }: ToolExecuteParams<typeof duplicateSubtreeToolSchema>): Promise<ToolResult> {
    try {
      const parseResult = idHelper.parseCardId(cardId);

      if (parseResult.ok === false) {
        return toolError(formatParseError(parseResult.error));
      }

      const numericId = parseResult.numericId;

      const state = store.getState();
      const nodes = state.campaignPage.campaignLogic.nodes[CampaignTreeType.Campaign];

      if (!nodes) {
        return toolError(
          `No nodes found for campaign tree type ${CampaignTreeType.Campaign}. Make sure you're in a campaign context.`,
        );
      }

      const rootNode = nodes[numericId];

      if (!rootNode) {
        return toolError(
          `Card ${idHelper.toCardId(numericId)} not found. Available card IDs: ${Object.keys(nodes)
            .map((id) => idHelper.toCardId(Number(id)))
            .join(', ')}`,
        );
      }

      const nextNodeId = state.campaignPage.campaignLogic.nextNodeId;

      if (!nextNodeId) {
        return toolError('Unable to determine next node ID');
      }

      const duplicationResult = duplicateSubtreeRecursive(rootNode, nextNodeId, nodes);

      const {duplicatedNodes} = duplicationResult;

      if (duplicatedNodes.length === 0) {
        return toolError('No nodes were duplicated');
      }

      const nodesToAdd = duplicatedNodes.map(({node}) => ({
        ...node,
        inputs: [],
        outputs: [],
      })) as MapNode[];

      const addResult = await store.dispatch(
        batchUpdateCards({
          nodesToAdd,
          nodesToUpdate: [],
          campaignTreeType: CampaignTreeType.Campaign,
        }),
      );

      if (!batchUpdateCards.fulfilled.match(addResult)) {
        const errorMessage = addResult.error?.message || 'Unknown error occurred';

        return toolError(`Failed to add duplicated cards: ${errorMessage}`);
      }

      const idMap = buildConnectionMap(duplicatedNodes);

      const nodesToUpdate: MapNode[] = [];

      for (const {originalId, newId, node} of duplicatedNodes) {
        const originalNode = nodes[originalId];

        if (!originalNode) {
          continue;
        }

        if (originalNode.outputs.length === 0) {
          continue;
        }

        const updatedOutputs: DisconnectedNodeOutputData[] = originalNode.outputs.map((output) => {
          if (output.nodeId === NO_NODE_ID) {
            return {...output, nodeId: NO_NODE_ID};
          }

          const newTargetId = idMap[output.nodeId];

          return {
            ...output,
            nodeId: newTargetId !== undefined ? newTargetId : NO_NODE_ID,
          };
        });

        nodesToUpdate.push({
          ...node,
          id: newId,
          outputs: updatedOutputs,
          inputs: [],
        } as MapNode);
      }

      if (nodesToUpdate.length > 0) {
        const updateResult = await store.dispatch(
          batchUpdateCards({
            nodesToAdd: [],
            nodesToUpdate,
            campaignTreeType: CampaignTreeType.Campaign,
          }),
        );

        if (!batchUpdateCards.fulfilled.match(updateResult)) {
          const errorMessage = updateResult.error?.message || 'Unknown error occurred';

          return toolError(`Failed to update connections: ${errorMessage}`);
        }
      }

      await refreshCampaignIssues(store);

      const rootCardIdentifier =
        rootNode.name || rootNode.description || idHelper.toCardId(numericId);
      const newCardIds = duplicatedNodes.map((d) => idHelper.toCardId(d.newId)).join(', ');

      return toolResult(
        `Successfully duplicated subtree rooted at card "${rootCardIdentifier}" (${idHelper.toCardId(numericId)}).\nCreated ${duplicatedNodes.length} new card(s) with IDs: ${newCardIds}.\nThe duplicated subtree has been positioned slightly offset from the original.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolError(`Error duplicating subtree: ${errorMessage}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.DuplicateSubtree,
    displayName: 'Duplicating Campaign Subtree',
    description:
      'Duplicates an entire subtree of campaign cards starting from a specified root card. Recursively duplicates all child cards and their connections until reaching creative nodes (leaf nodes) or the end of the flow. The duplicated subtree will maintain all internal connections and be positioned slightly offset from the original.',
    execute,
    inputSchema: duplicateSubtreeToolSchema,
  });
}

export default duplicateSubtreeTool;
